import type { ComponentType } from 'react';

/**
 * ★★ THE PER-MODULE SEAM. Phase 7 run 1 (shell).
 *
 * The legacy equivalent is `api/api.js`'s `openCmsTabs` / `openProductTabs`
 * object plus `workflow/cms.detail.js`'s 61 `define("open.view.*")` shims: a
 * flat map of TAB KEY → "lazy-load this view once, then show it".
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * A TRANCHE'S ENTIRE INTEGRATION STEP IS:
 *   1. add `pages/cms-admin/screens/<Module>Screen.tsx`
 *   2. change that screen's `status` from 'pending' to 'live' and point
 *      `load` at it
 * Nothing else in the shell changes. That is the whole contract.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * `status` values:
 *  - `'live'`    — ported and verified; `load` is set.
 *  - `'pending'` — not yet ported; renders a labelled placeholder.
 *  - `'dead'`    — PROVEN unreachable in the legacy app (survey §2.1). Present
 *                  here only so a future reader does not "discover" it and port
 *                  it by mistake. Never rendered, never given a nav row.
 *
 * `section` mirrors the two DOM hosts in `tmpl/cms_app.tmpl`:
 *  - `'admin'`   → `#menuTab`      (dispatch: nav → `open:<tab>` → `api.js`)
 *  - `'product'` → `#productsTabs` (dispatch: nav → `click:product:item` →
 *                   `product.menu.js:open_tabs` → `require(['view.'+tab])`)
 * ★ These are genuinely two different dispatch paths in legacy, not a style
 * choice — see survey §0 correction 3.
 *
 * `hashParent` is the `parent` segment `product.menu.js:open_tabs` puts in the
 * hash for product screens (`#products/<id>/<parent>/<tab>`). Admin screens
 * have none — their hash is bare `#<tab>`.
 */
export type CmsScreenStatus = 'live' | 'pending' | 'dead';
export type CmsScreenSection = 'admin' | 'product';
export type CmsHashParent =
  | 'settings'
  | 'construction'
  | 'modular_frame'
  | 'components'
  | 'accessories';

export interface CmsScreen {
  /**
   * The DOM slot id, and — for all but one screen — also the nav anchor `value=`
   * and the hash fragment.
   *
   * ★ T2 CORRECTION (2026-08-05): this used to be documented as "the single
   * source of identity". It is not. `all_products` proves the three identifiers
   * are genuinely independent:
   *     nav anchor   `<a value="products" class="nav-heading">`  main.menu.tmpl:190
   *     hash         `Backbone.history.navigate("products")`     api/api.js:750
   *     DOM slot id  `<div id="all_products" class="col-xs-12">` cms_app.tmpl:34
   * and the slot id is load-bearing in CSS (`styles.css:4904-4955` keys the whole
   * product-tile grid off `#all_products`), so it cannot simply be renamed.
   * Where they diverge, `key` stays the SLOT ID and `routeKey` carries the hash.
   */
  key: string;
  /**
   * The hash fragment this screen is addressed by, when it is NOT `key`.
   * Defaults to `key`. Read `cmsRouteKey(screen)` rather than this field.
   *
   * Added in T2 because the React shell could not open `all_products` AT ALL:
   * `parseCmsHash('#products')` looked up `products`, found nothing, and rendered
   * a blank content area where legacy shows the screen. Measured cold, side by
   * side: `:8130 /admin/#products` → slot visible with 384 chars; the un-repaired
   * shell → hash kept, slot empty and `display:none`.
   */
  routeKey?: string;
  /** Which host renders it. */
  section: CmsScreenSection;
  /** Legacy module directory under `cms/mvc/`, for traceability. */
  module: string;
  /** Migration tranche from `frontend/PHASE7_CMS_SURVEY.md` §5. */
  tranche: string;
  status: CmsScreenStatus;
  /** Product-section hash segment; omitted for admin screens. */
  hashParent?: CmsHashParent;
  /**
   * Extra class(es) on the screen's DOM slot, ON TOP of `col-xs-12`.
   *
   * ★ ADDED IN T1 (2026-08-05) — a real shell gap, found by `numbering`.
   * `tmpl/cms_app.tmpl` gives every slot `class="col-xs-12"` with exactly TWO
   * exceptions (`:22-23`):
   *     <div id="job_format"   class="job-format col-xs-12">
   *     <div id="quote_format" class="quote-format col-xs-12">
   * and those classes are **load-bearing, not decoration**:
   *     styles.css:14903  .job-format .cms_defined_input,
   *                       .quote-format .cms_defined_input { display: none }
   *     styles.css:14907  .job-format .with_input .cms_defined_input,
   *                       .quote-format .with_input .cms_defined_input { display: block }
   * i.e. that pair IS the show/hide mechanism for the numbering value box.
   * Measured without it: all 8 inputs render `inline-block` at 177 px instead of
   * 4 hidden / 4 shown.
   *
   * Kept on the registry rather than as a wrapper `<div>` in the screen, because
   * a wrapper would insert a node legacy does not have — the class belongs to the
   * slot, and the slot belongs to the host.
   */
  slotClass?: string;
  /** Set when `status === 'live'`. */
  load?: () => Promise<{ default: ComponentType<CmsScreenProps> }>;
  /** Why it is dead — required when `status === 'dead'`. */
  deadReason?: string;
}

export interface CmsScreenProps {
  screenKey: string;
  productId: number | string;
}

/**
 * ★ Every entry below carries its REAL legacy tab key, verified against the
 * live nav on 2026-08-05 (67 anchors rendered on `/admin/` for mfg 1).
 * Data-gated product rows (`floor_drain`, `door_switch`, `split_plenum`) are
 * listed as `pending` — their code is live, but the nav row only appears if the
 * manufacturer's catalog enables them (survey §2.2).
 */
export const CMS_SCREENS: CmsScreen[] = [
  // ── Administrative (#menuTab) ─────────────────────────────────────────────
  // ★ The BOOT screen — `CMS_DEFAULT_SCREEN`, opened by an empty fragment.
  // ★ T2 measured that 21 of its 27 legacy files are DEAD: the entire terms
  // block (3 buttons + `<div id="terms">`) is inside an HTML COMMENT in
  // `tmpl/mfg.tmpl:135-149`, so `view/terms.js` + `model/terms.js` never run and
  // this screen NEVER touches `/ahu/terms/` — there is no collision with the
  // T1 `document` screen. The 6 departments files mount on `#mfg_deps_content`,
  // which exists in zero templates; `view/comp.js` has no requirer;
  // `view/dep.users.js` is a 0-byte file; `view/new_market.js` does not exist.
  {
    key: 'manufacturer',
    section: 'admin',
    module: 'manufacturer',
    tranche: 'T2',
    status: 'live',
    load: () => import('./screens/manufacturer/ManufacturerScreen'),
  },
  // ★ A SECURITY SURFACE: user CRUD + per-user product entitlements. Unlike
  // `manufacturer`, ALL 18 of its legacy files are live — no dead code.
  // ⚠ Its user delete is `POST /api/v1.0/users/<id>/delete/` (a Celery task),
  // NOT a DELETE — `api/rest_user/urls.py:16` comments the DELETE verb out
  // ("You can't just delete user"). See `networkApi.ts`.
  {
    key: 'network',
    section: 'admin',
    module: 'network',
    tranche: 'T2',
    status: 'live',
    load: () => import('./screens/network/NetworkScreen'),
  },
  // ═══ T5 — the SECURITY tranche. These seven decide which roles exist, who
  // holds them, and what each role may see on the dashboard, project board, AHU
  // board, reports and workflow.
  // ★ The five BOARD editors are near-twins (main views 116-125 LOC; 44 differing
  // lines dashboard↔reports, 36 ahu↔project), so they are ONE parameterised
  // implementation — `PermissionJoinEditor` + a config each — at **97.8% shared**.
  // `roles` and `access` stay bespoke; forcing them in would have needed a config
  // with more branches than shared code.
  // ⚠ NO `slotClass` on any of the seven — measured live, all are plain
  // `col-xs-12`.
  // ★★ The flat `permissions/view/` + `permissions/tmpl/` fork is DEAD except
  // FOUR files (the survey said two): `view/dashboard_item.js`,
  // `view/delete_role.js`, `tmpl/delete_role.tmpl` and — the one the survey
  // missed — `tmpl/access_notes_popup.tmpl`, which the LIVE
  // `access/view/access_notes_popup.js:6` requires and which differs from its
  // nested twin in SIX lines of user-visible copy. Two NESTED files are dead in
  // exchange: `dashboard/view/dashboard_item.js` (superseded by the flat one, and
  // missing its `this.state` reset) and `access/tmpl/access_notes_popup.tmpl`.
  {
    key: 'permissions_roles', section: 'admin', module: 'permissions/roles', tranche: 'T5',
    status: 'live', load: () => import('./screens/permissions/RolesScreen'),
  },
  {
    key: 'permissions_access', section: 'admin', module: 'permissions/access', tranche: 'T5',
    status: 'live', load: () => import('./screens/permissions/AccessScreen'),
  },
  {
    key: 'permissions_dashboard', section: 'admin', module: 'permissions/dashboard', tranche: 'T5',
    status: 'live', load: () => import('./screens/permissions/DashboardPermissionsScreen'),
  },
  {
    key: 'permissions_project', section: 'admin', module: 'permissions/project', tranche: 'T5',
    status: 'live', load: () => import('./screens/permissions/ProjectPermissionsScreen'),
  },
  {
    key: 'permissions_ahu', section: 'admin', module: 'permissions/ahu', tranche: 'T5',
    status: 'live', load: () => import('./screens/permissions/AhuPermissionsScreen'),
  },
  {
    key: 'permissions_reports', section: 'admin', module: 'permissions/reports', tranche: 'T5',
    status: 'live', load: () => import('./screens/permissions/ReportsPermissionsScreen'),
  },
  {
    key: 'permissions_workflow', section: 'admin', module: 'permissions/workflow', tranche: 'T5',
    status: 'live', load: () => import('./screens/permissions/WorkflowPermissionsScreen'),
  },
  // ★ The parent `numbering` screen is DEAD (survey §2.1) — only these two are
  // live. Both carry a `slotClass`: they are the ONLY two slots in
  // `tmpl/cms_app.tmpl` with a class beyond `col-xs-12`, and it is load-bearing.
  //
  // ★★ THE ORDER IS LOAD-BEARING TOO: `job_format` FIRST, then `quote_format`,
  // exactly as `tmpl/cms_app.tmpl:22-23` emits them. This array is what
  // `CmsScreenHost` maps into `#menuTab`, so it sets the slots' DOCUMENT ORDER,
  // and both numbering templates give their ADD button the SAME
  // `id="add_button"` (legacy has that duplicate too). Neither numbering view
  // resolves it globally — legacy scopes it as
  // `self.$el[0].querySelector('#add_button')`, React holds it in JSX — but
  // `settings/dynamicSizer/sizer_optimization/view/sizer.js:72` does
  // `document.getElementById("add_button")` UNSCOPED, and its own template
  // (`cms_sizer.tmpl:11`) reuses the id a third time. `getElementById` returns
  // the FIRST match in document order, so once either numbering tab has been
  // opened that lookup lands on a numbering ADD button — on legacy,
  // `job_format`'s. Listing `quote_format` first handed React `quote_format`'s
  // button instead, i.e. the Dynamic Sizer's `non-clickable` validity gate
  // would grey out a different screen's button than it does on legacy.
  // Latent today (`settDynamicSizer` is a later tranche); fixed at the source.
  // Found by T1's independent verifier, 2026-08-05.
  {
    key: 'job_format',
    section: 'admin',
    module: 'numbering',
    tranche: 'T1',
    status: 'live',
    slotClass: 'job-format',
    load: () => import('./screens/numbering/JobFormatScreen'),
  },
  {
    key: 'quote_format',
    section: 'admin',
    module: 'numbering',
    tranche: 'T1',
    status: 'live',
    slotClass: 'quote-format',
    load: () => import('./screens/numbering/QuoteFormatScreen'),
  },
  {
    key: 'workflow',
    section: 'admin',
    module: 'workflow.app',
    tranche: 'T1',
    status: 'live',
    load: () => import('./screens/workflow/WorkflowScreen'),
  },
  // ★ THE COST-CHART TWINS — one parameterised implementation under
  // `screens/cost-charts/`, not two screens. `cost_codes` and `activities` are
  // the same module copy-pasted with the nouns changed (measured: `view/groups.js`
  // 209 vs 209 lines with 14 differing; `view/groups_and_codes.js` 286 vs 286
  // with 46). 85% of the port is shared; only `CodesTab`'s two row renderers are
  // per-family, because `view/cost_and_description.js` genuinely diverges
  // (237 vs 274 lines, 99 differing — activities has 4 extra columns and 2 extra
  // master collections).
  //
  // ★★ ORDER IS LOAD-BEARING: `costs` MUST precede `activities`, matching
  // `tmpl/cms_app.tmpl`. Both modules' Groups tabs resolve their ADD button with
  // the document-wide `document.getElementsByClassName('btn_add_group')[0]`, and
  // the class exists in BOTH — so slot document order decides which screen's
  // button each disables. Measured on `:8130`: with costs mounted first,
  // activities' Groups view puts `non-clickable` on COSTS' button. Reproduced.
  {
    key: 'costs',
    section: 'admin',
    module: 'cost_codes',
    tranche: 'T3',
    status: 'live',
    load: () => import('./screens/cost-charts/CostsScreen'),
  },
  {
    key: 'activities',
    section: 'admin',
    module: 'activities',
    tranche: 'T3',
    status: 'live',
    load: () => import('./screens/cost-charts/ActivitiesScreen'),
  },
  {
    key: 'allocations',
    section: 'admin',
    module: 'allocations',
    tranche: 'T1',
    status: 'live',
    load: () => import('./screens/allocations/AllocationsScreen'),
  },
  // ★ T4's consumer-and-proof. `componentTable` is a LIBRARY (3,964 LOC, no nav
  // anchor, no slot, no registry entry), so it cannot be verified on its own —
  // `multipliers` is the smallest real consumer and the only honest way to sign
  // it off. The library lives at `components/component-table/`, deliberately NOT
  // under `screens/`, because T5–T8 bring 12–21 consumers each.
  // ⚠ 6 of `multipliers`' 10 legacy files are DEAD, including its BIGGEST
  // (`view/cost_multipliers.js`, 272 LOC — reachable only through a `default:`
  // branch that the `case` above it intercepts). Live surface is
  // `view/multipliers.js` (86 LOC) + 3 templates.
  {
    key: 'multipliers',
    section: 'admin',
    module: 'multipliers',
    tranche: 'T4',
    status: 'live',
    load: () => import('./screens/multipliers/MultipliersScreen'),
  },
  {
    key: 'freight',
    section: 'admin',
    module: 'freight',
    tranche: 'T1',
    status: 'live',
    load: () => import('./screens/freight/FreightScreen'),
  },
  {
    key: 'document',
    section: 'admin',
    module: 'document',
    tranche: 'T1',
    status: 'live',
    load: () => import('./screens/document/DocumentScreen'),
  },
  // ★ T9 — the 3 `kinetix_ai` standalone admin forms. `knowledge_base` + `insights`
  // are domain-gated (allowlist in `screens/kinetix-ai/domainGate.ts`): full UI
  // only on {thermal,demo,preselect,staging}.kinetix-air.com | localhost, else a
  // "Contact your sales rep" fallback — so on the 127.0.0.1 harness they render
  // the FALLBACK (measured). `usage_statistics` is NOT gated (renders full) and
  // reuses T2's shared `manufacturer` model (`/api/v1.0/mfg/<id>`).
  { key: 'kinetix_ai_knowledge_base', section: 'admin', module: 'kinetix_ai', tranche: 'T1/T9', status: 'live', load: () => import('./screens/kinetix-ai/KnowledgeBaseScreen') },
  { key: 'kinetix_ai_insights', section: 'admin', module: 'kinetix_ai', tranche: 'T1/T9', status: 'live', load: () => import('./screens/kinetix-ai/InsightsScreen') },
  { key: 'kinetix_ai_usage_statistics', section: 'admin', module: 'kinetix_ai', tranche: 'T1/T9', status: 'live', load: () => import('./screens/kinetix-ai/UsageStatisticsScreen') },
  // ★ LAST, matching `tmpl/cms_app.tmpl` — `all_products` is emitted AFTER the
  // three `kinetix_ai_*` slots, not before them. Moved here in T1 for the same
  // reason `job_format` precedes `quote_format` above: this array sets the slots'
  // DOCUMENT ORDER, and legacy code resolves duplicate ids with
  // `document.getElementById`, which returns the first match in document order.
  // ★ The one screen whose three identifiers all differ (see `routeKey` above).
  // ⚠ Reachable ONLY by hash: its nav anchor is a section HEADING carrying
  // `class="nav-heading"`, and `skeleton.css:602` gives that `pointer-events:
  // none`. Measured on :8130 — a real mouse press on it produces 0 requests and
  // leaves the hash alone; legacy opens this screen only via `router.js`'s
  // SYNTHESISED jQuery click, which bypasses `pointer-events`.
  {
    key: 'all_products',
    routeKey: 'products',
    section: 'admin',
    module: 'products',
    tranche: 'T2',
    status: 'live',
    load: () => import('./screens/products/ProductsScreen'),
  },

  // ── Product → settings (#productsTabs) ── T6: the FIRST product-section screens
  { key: 'settProduct', section: 'product', hashParent: 'settings', module: 'settings/product', tranche: 'T6', status: 'live', load: () => import('./screens/settings/SettProductScreen') },
  { key: 'settDynamicSizer', section: 'product', hashParent: 'settings', module: 'settings/dynamicSizer', tranche: 'T6', status: 'live', load: () => import('./screens/settings/SettDynamicSizerScreen') },
  { key: 'settOptions', section: 'product', hashParent: 'settings', module: 'settings/options', tranche: 'T6', status: 'live', load: () => import('./screens/settings/SettOptionsScreen') },
  { key: 'settModelView', section: 'product', hashParent: 'settings', module: 'settings/modelView', tranche: 'T6', status: 'live', load: () => import('./screens/settings/SettModelViewScreen') },
  { key: 'settManufacturing', section: 'product', hashParent: 'settings', module: 'settings/manufacturing', tranche: 'T6', status: 'live', load: () => import('./screens/settings/SettManufacturingScreen') },
  { key: 'settWeight', section: 'product', hashParent: 'settings', module: 'settings/weight', tranche: 'T6', status: 'live', load: () => import('./screens/settings/SettWeightScreen') },
  { key: 'settCost', section: 'product', hashParent: 'settings', module: 'settings/cost', tranche: 'T6', status: 'live', load: () => import('./screens/settings/SettCostScreen') },

  // ── Product → construction ────────────────────────────────────────────────
  { key: 'cnstrSettings', section: 'product', hashParent: 'construction', module: 'constructions/cnstrSettings', tranche: 'T7', status: 'live', load: () => import('./screens/constructions/CnstrSettingsScreen') },
  { key: 'rawMaterial', section: 'product', hashParent: 'construction', module: 'constructions/rawMaterial', tranche: 'T7', status: 'live', load: () => import('./screens/constructions/RawMaterialScreen') },
  { key: 'cabinet', section: 'product', hashParent: 'construction', module: 'constructions/cabinet', tranche: 'T7', status: 'live', load: () => import('./screens/constructions/CabinetScreen') },
  // ★ base is the ONLY screen with a 5–6-segment hash (`…/construction/base/<sub>/<last>`)
  // sourced from localStorage; its shell hash support is in useCmsHashRoute.ts.
  { key: 'base', section: 'product', hashParent: 'construction', module: 'constructions/base', tranche: 'T7', status: 'live', load: () => import('./screens/constructions/BaseScreen') },
  { key: 'lugs', section: 'product', hashParent: 'construction', module: 'constructions/lugs', tranche: 'T7', status: 'live', load: () => import('./screens/constructions/LugsScreen') },

  // ── Product → modular frame ── T6. mf_parts is the first grid consumer other
  // than multipliers; mf_rules is scoped to its ONE visible tab (13 of 14 ship
  // class="hide"). mf_sett lists the grid in its define() deps but never uses it.
  { key: 'mf_parts', section: 'product', hashParent: 'modular_frame', module: 'modular_frame/parts', tranche: 'T6', status: 'live', load: () => import('./screens/modular-frame/MfPartsScreen') },
  { key: 'mf_sett', section: 'product', hashParent: 'modular_frame', module: 'modular_frame/settings', tranche: 'T6', status: 'live', load: () => import('./screens/modular-frame/MfSettScreen') },
  { key: 'mf_rules', section: 'product', hashParent: 'modular_frame', module: 'modular_frame/rules', tranche: 'T6', status: 'live', load: () => import('./screens/modular-frame/MfRulesScreen') },

  // ── Product → components ──────────────────────────────────────────────────
  { key: 'fans', section: 'product', hashParent: 'components', module: 'components/fans', tranche: 'T9a', status: 'live', load: () => import('./screens/components/fans/FansScreen') },
  { key: 'coils', section: 'product', hashParent: 'components', module: 'components/coils', tranche: 'T9b', status: 'live', load: () => import('./screens/components/CoilsScreen') },
  { key: 'filters', section: 'product', hashParent: 'components', module: 'components/filters', tranche: 'T9b', status: 'live', load: () => import('./screens/components/FiltersScreen') },
  { key: 'eliminators', section: 'product', hashParent: 'components', module: 'components/eliminators', tranche: 'T9b', status: 'live', load: () => import('./screens/components/EliminatorsScreen') },
  { key: 'evap_packs', section: 'product', hashParent: 'components', module: 'components/evapPacks', tranche: 'T9b', status: 'live', load: () => import('./screens/components/EvapPacksScreen') },
  { key: 'gas_heaters', section: 'product', hashParent: 'components', module: 'components/gasHeaters', tranche: 'T9b', status: 'live', load: () => import('./screens/components/GasHeatersScreen') },
  { key: 'er_wheel', section: 'product', hashParent: 'components', module: 'components/energy_wheels', tranche: 'T9b', status: 'live', load: () => import('./screens/components/ErWheelScreen') },
  { key: 'plate_heat_exchangers', section: 'product', hashParent: 'components', module: 'components/plate_heat_exchanger', tranche: 'T9b', status: 'live', load: () => import('./screens/components/PlateHeatExchangersScreen') },
  { key: 'silencers', section: 'product', hashParent: 'components', module: 'components/silencers', tranche: 'T9b', status: 'live', load: () => import('./screens/components/SilencersScreen') },
  // ★ The ONLY genuinely data-gated screen left (survey §2.2). It IS master
  // component id 11 `Split-Plenum`, so any manufacturer could enable it; mfg 1
  // has not, so no nav row renders on dev3 and it cannot be verified here. At
  // 300 LOC it is cheaper to port than to ask about — T9b ports it and it stays
  // dormant-by-data. Do NOT confuse this with the `dead` entries below.
  { key: 'split_plenum', section: 'product', hashParent: 'components', module: 'components/split', tranche: 'T9b', status: 'live', load: () => import('./screens/components/SplitPlenumScreen') },

  // ── Product → accessories ─────────────────────────────────────────────────
  { key: 'access_doors', section: 'product', hashParent: 'accessories', module: 'accessories/accessDoors', tranche: 'T8d', status: 'live', load: () => import('./screens/accessories/AccessDoorsScreen') },
  { key: 'openings', section: 'product', hashParent: 'accessories', module: 'accessories/openings', tranche: 'T8b', status: 'live', load: () => import('./screens/accessories/OpeningsScreen') },
  { key: 'dampers', section: 'product', hashParent: 'accessories', module: 'accessories/dampers', tranche: 'T8b', status: 'live', load: () => import('./screens/accessories/DampersScreen') },
  { key: 'louvers', section: 'product', hashParent: 'accessories', module: 'accessories/louvers', tranche: 'T8b', status: 'live', load: () => import('./screens/accessories/LouversScreen') },
  { key: 'combo_louvers', section: 'product', hashParent: 'accessories', module: 'accessories/combo_louvers', tranche: 'T8b', status: 'live', load: () => import('./screens/accessories/ComboLouversScreen') },
  { key: 'hoods', section: 'product', hashParent: 'accessories', module: 'accessories/hood', tranche: 'T8b', status: 'live', load: () => import('./screens/accessories/HoodScreen') },
  { key: 'dog_houses', section: 'product', hashParent: 'accessories', module: 'accessories/dog_houses', tranche: 'T8d', status: 'live', load: () => import('./screens/accessories/DogHousesScreen') },
  { key: 'adp_gage', section: 'product', hashParent: 'accessories', module: 'accessories/adp_gage', tranche: 'T8a', status: 'live', load: () => import('./screens/accessories/AdpGageScreen') },
  { key: 'jbox', section: 'product', hashParent: 'accessories', module: 'accessories/jbox', tranche: 'T8a', status: 'live', load: () => import('./screens/accessories/JboxScreen') },
  { key: 'disconnect', section: 'product', hashParent: 'accessories', module: 'accessories/disconnect', tranche: 'T8a', status: 'live', load: () => import('./screens/accessories/DisconnectScreen') },
  { key: 'enclosure', section: 'product', hashParent: 'accessories', module: 'accessories/enclosure', tranche: 'T8a', status: 'live', load: () => import('./screens/accessories/EnclosureScreen') },
  { key: 'vfd', section: 'product', hashParent: 'accessories', module: 'accessories/vfd', tranche: 'T8a', status: 'live', load: () => import('./screens/accessories/VfdScreen') },
  { key: 'starter', section: 'product', hashParent: 'accessories', module: 'accessories/starter', tranche: 'T8a', status: 'live', load: () => import('./screens/accessories/StarterScreen') },
  { key: 'light', section: 'product', hashParent: 'accessories', module: 'accessories/light', tranche: 'T8e', status: 'live', load: () => import('./screens/accessories/LightScreen') },
  { key: 'drain_pan', section: 'product', hashParent: 'accessories', module: 'accessories/drainPan', tranche: 'T8c', status: 'live', load: () => import('./screens/accessories/DrainPanScreen') },
  { key: 'uvc', section: 'product', hashParent: 'accessories', module: 'accessories/uvc', tranche: 'T8e', status: 'live', load: () => import('./screens/accessories/UvcScreen') },
  { key: 'user_defined_accessory', section: 'product', hashParent: 'accessories', module: 'accessories/user_defined_accessory', tranche: 'T8e', status: 'live', load: () => import('./screens/accessories/UserDefinedAccessoryScreen') },
  { key: 'viewport', section: 'product', hashParent: 'accessories', module: 'accessories/viewport', tranche: 'T8d', status: 'live', load: () => import('./screens/accessories/ViewportScreen') },
  { key: 'piping_accessorie', section: 'product', hashParent: 'accessories', module: 'accessories/piping_accessories', tranche: 'T8e', status: 'live', load: () => import('./screens/accessories/PipingAccessorieScreen') },
  // (`floor_drain` and `door_switch` moved to the DEAD block below — resolved
  //  2026-08-05 against the master catalog, see survey §2.2.)

  // ── Product → special options (top-level, no group) ───────────────────────
  // ★ No `hashParent` — its hash is `#products/<id>/special_options` (router.js:26),
  // a different shape from every other product screen. The shell builds it from
  // the absent hashParent; do not add one.
  { key: 'special_options', section: 'product', module: 'special_options', tranche: 'T6', status: 'live', load: () => import('./screens/special-options/SpecialOptionsScreen') },

  // ── PROVEN DEAD — listed so nobody re-discovers and ports them ────────────
  {
    key: 'partners',
    section: 'admin',
    module: 'partners',
    tranche: '—',
    status: 'dead',
    deadReason:
      'No `value="partners"` anchor in either menu template (confirmed against the live nav). ' +
      'DOM slot commented out (tmpl/cms_app.tmpl:9-11); `open.view.partners` define()d nowhere; ' +
      'app.js:152-156 path block commented out. UNRELATED to the migrated React /partners/ page, ' +
      'which ports django/partners/ (a project CRM on /api/v1.0/projects/*).',
  },
  {
    key: 'users',
    section: 'admin',
    module: 'users',
    tranche: '—',
    status: 'dead',
    deadReason:
      'Orphaned: dispatcher-wired and slot present, but no `value="users"` anchor anywhere, ' +
      'so it is reachable only by hand-typing #users. ★ RESOLVED BY GIT 2026-08-05 — no product ' +
      'call needed: the anchor was deleted 2017-11-13 in commit 16676df76f, titled "Remove ' +
      "'Users' and 'Partners' from CMS: Main Menu\" — users/ and partners/ were retired TOGETHER, " +
      'deliberately, 8.5 years ago. Nothing in the repo links to #users. Superseded by the live ' +
      'Mfg Network screen, which does the same user CRUD against the modern /api/v1.0/users/ ' +
      'while users/ still points at the stale /api/v1.0/mfg/1/users/.',
  },
  {
    key: 'floor_drain',
    section: 'product',
    hashParent: 'accessories',
    module: 'accessories/floorDrain',
    tranche: '—',
    status: 'dead',
    deadReason:
      'No `floor_drain` row in `ksdm_master_ahu_accessory` (17 rows), so the catalog-driven nav ' +
      'can never emit its anchor for ANY manufacturer. The anchor was deleted 2019-03-13 in ' +
      '88528c658d, the commit that made the nav catalog-driven. Its backend routes ' +
      '(api/rest_cms/urls_floor_drains.py) survive, orphaned. 869 LOC.',
  },
  {
    key: 'door_switch',
    section: 'product',
    hashParent: 'accessories',
    module: 'accessories/doorSwitch',
    tranche: '—',
    status: 'dead',
    deadReason:
      'No master accessory row, and not standalone in the backend either — door switches are ' +
      'nested under access doors (api/rest_cms/urls_access_doors.py:82-96, ' +
      '/access_doors/door_switches/). The standalone 167-LOC screen is unreachable.',
  },
  {
    key: 'conventions',
    section: 'product',
    module: 'conventions',
    tranche: '—',
    status: 'dead',
    deadReason:
      'No `conventions`/`cnv` anchor in the live nav. dispatcher.js:45 binds ' +
      'api.openProductTabs.open_conventions, which is undefined (api.openProductTabs has only ' +
      'open_settings). Its only loader, product.menu.js:open_single_tabs, is bound to no event.',
  },
  {
    key: 'roof_curb',
    section: 'product',
    hashParent: 'construction',
    module: 'constructions/roofCurb',
    tranche: '—',
    status: 'dead',
    deadReason:
      '1,282 LOC of real code with a path map, a DOM slot and a breadcrumb case — but the ' +
      '<ul id="construction"> block has exactly five anchors and roof_curb is not one. Deep links ' +
      'do not help: router.js:47 synthesises a click on an anchor that does not exist. ' +
      '★★ DEAD END-TO-END, proven on four legs 2026-08-05, so there is no product question here: ' +
      '(a) anchor deleted 2020-09-16 in e71e11cabc "MODULAR FRAME"; (b) ALL NINE roof-curb ' +
      'catalogue tables hold 0 rows (6 ksdm_cms_ahu_curb_* + 3 ksdm_master_ahu_roof_curb_*), so ' +
      'the page had nothing to configure anyway; (c) the AHU BOARD\'s own Curb tab is dead too — ' +
      'ahu/app.js:67 maps view.tab.ahu.curb to view/tabs/tab/ahu.curb, a path that does not exist ' +
      '(real file: view/tabs/tab.ahu.curb.js), create.view.ahu.curb.tab is never required, and ' +
      'there is no nav button — so Phase 6 correctly omitted it and this is NOT a regression we ' +
      'shipped; (d) of 3,645 ksdm_ahu_curb rows, build_id/shape_id/slope/height_high_one are ' +
      'populated 0 times (only mtl_gauge_id, 3,629, pointing at sheet metal). Nothing in cad/, ' +
      'report/ or the React AHU board references curbs.',
  },
  {
    key: 'plenum',
    section: 'product',
    hashParent: 'components',
    module: 'components (n/a)',
    tranche: '—',
    status: 'dead',
    deadReason:
      'In mfg 1\'s component catalog but explicitly filtered OUT of the nav by ' +
      'tmpl/product.menu.tmpl:144 `{{#ifCompare value \'!=\' \'plenum\'}}`. Confirmed absent live.',
  },
  {
    key: 'enclosures',
    section: 'product',
    hashParent: 'accessories',
    module: 'accessories/enclosures',
    tranche: '—',
    status: 'dead',
    deadReason:
      '41-LOC stub that renders a breadcrumb and an empty .cms-content-item with `events: {}`. ' +
      'Superseded by accessories/enclosure/ (1,153 LOC). The exact `Manufacturing` shape.',
  },
  {
    key: 'ams',
    section: 'product',
    hashParent: 'accessories',
    module: 'accessories/ams',
    tranche: '—',
    status: 'dead',
    deadReason:
      'app.js:876 maps view.ams -> accessories/ams/view/ams; THAT FILE DOES NOT EXIST. The AMS ' +
      'templates/models/collections ARE live, but inside the Fans tranche ' +
      '(components/fans/view/airflows.js:54) — port them there, not as a page.',
  },
];

/** Fast lookup by tab key (= DOM slot id). */
export const CMS_SCREENS_BY_KEY: Record<string, CmsScreen> = Object.fromEntries(
  CMS_SCREENS.map((s) => [s.key, s])
);

/**
 * The hash fragment a screen is addressed by. Equal to `key` for every screen
 * except `all_products` — see the `routeKey` docs above.
 */
export const cmsRouteKey = (s: CmsScreen): string => s.routeKey ?? s.key;

/** Fast lookup by HASH fragment. Use this when resolving a URL, not `BY_KEY`. */
export const CMS_SCREENS_BY_ROUTE_KEY: Record<string, CmsScreen> = Object.fromEntries(
  CMS_SCREENS.map((s) => [cmsRouteKey(s), s])
);

/** Screens a user can actually reach (i.e. everything not proven dead). */
export const isReachable = (s: CmsScreen): boolean => s.status !== 'dead';

/** The boot screen — `router.js:88 start()` fires `open:manufacturer`. */
export const CMS_DEFAULT_SCREEN = 'manufacturer';
